import React, {useEffect, useMemo, useState} from 'react';
import {Box, Button, Grid, MenuItem, Paper, TextField, Typography} from '@mui/material';
import StateSelector from "../core/components/StateSelector";

const defaultFormState = {
    first: '',
    last: '',
    dob: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    status: 'ACTIVE',
};

const DefendantForm = ({ onSubmit, initialValues, submitLabel = 'Add Defendant', disablePaper = false }) => {
    const initialFormValues = useMemo(() => ({
        ...defaultFormState,
        ...(initialValues ?? {}),
        status: initialValues?.status || 'ACTIVE',
    }), [initialValues]);

    const [formData, setFormData] = useState(initialFormValues);

    const [errors, setErrors] = useState({}); // Error state

    useEffect(() => {
        setFormData(initialFormValues);
    }, [initialFormValues]);

    const validate = () => {
        const newErrors = {};
        if (!formData.first) newErrors.first = "First name is required.";
        if (!formData.last) newErrors.last = "Last name is required.";
        if (!formData.dob) newErrors.dob = "Date of birth is required.";
        if (!formData.phone) newErrors.phone = "Phone number is required.";
        if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "A valid email is required.";
        // Conditional validation
        if (formData.address) {
            if (!formData.state) newErrors.state = "State is required when address is provided.";
            if (!formData.city) newErrors.city = "City is required when address is provided.";
            if (!formData.zip) newErrors.zip = "ZIP code is required when address is provided.";
        }
        return newErrors;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors); // Set errors if validation fails
        } else {
            setErrors({}); // Clear errors if validation passes
            onSubmit({...formData}); // Submit form data if validation passes
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
        setErrors({}); // Clear error when user starts typing
    };

    const formContent = (
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 2 }}>
                <Typography variant="h5" gutterBottom>
                    Defendant Info
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            select
                            fullWidth
                            name="status"
                            label="Status"
                            value={formData.status}
                            onChange={handleChange}
                        >
                            <MenuItem value="ACTIVE">Active</MenuItem>
                            <MenuItem value="INACTIVE">Inactive</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            name="first"
                            label="First Name"
                            value={formData.first}
                            onChange={handleChange}
                            required
                            error={!!errors.first}
                            helperText={errors.first}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            name="last"
                            label="Last Name"
                            value={formData.last}
                            onChange={handleChange}
                            required
                            error={!!errors.last}
                            helperText={errors.last}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            name="dob"
                            label="Date of Birth"
                            type="date"
                            value={formData.dob}
                            onChange={handleChange}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            required
                            error={!!errors.dob}
                            helperText={errors.dob}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            name="phone"
                            label="Phone Number"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            error={!!errors.phone}
                            helperText={errors.phone}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            name="email"
                            label="Email Address"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            error={!!errors.email}
                            helperText={errors.email}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            name="address"
                            label="Address"
                            value={formData.address}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            fullWidth
                            name="city"
                            label="City"
                            value={formData.city}
                            required={!!formData.address}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <StateSelector
                            name="state"
                            value={formData.state}
                            onChange={handleChange}
                            error={!!errors.state}
                            required={!!formData.address}
                            helperText={errors.state}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            fullWidth
                            name="zip"
                            label="ZIP Code"
                            value={formData.zip}
                            onChange={handleChange}
                            required={!!formData.address}
                            error={!!errors.zip}
                            helperText={errors.zip}
                        />
                    </Grid>
                </Grid>
                <Box sx={{ mt: 3 }}>
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                        sx={{ color: 'common.white' }}
                    >
                        {submitLabel}
                    </Button>
                </Box>
            </Box>
    );

    if (disablePaper) {
        return formContent;
    }

    return (
        <Paper sx={{ p: 3, maxWidth: 600, margin: '0 auto' }}>
            {formContent}
        </Paper>
    );
};

export default DefendantForm;
